// pages/admin/invitations/InvitationsPage.tsx
import React, { useState, useEffect } from "react";
import { Search, Loader2, ChevronLeft, ChevronRight, Mail, Plus, Trash2, RefreshCw, Link2, Copy } from "lucide-react";
import PageListLayout from "../../../layouts/PageListLayout";
import InviteAgentModal from "../../../components/features/invitations/InviteAgentModal";
import useInvitations from "../../../hooks/features/invitations/useInvitations";
import { Invitation } from "../../../types/features/Invitation";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import InvitationsList from "../../../components/features/invitations/InvitationsList";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";

export default function InvitationsPage() {

  const { 
    invitations, 
    loading, 
    pagination, 
    getInvitations, 
    deleteInvitation,
    resendEmail,
    regenerateLink 
  } = useInvitations();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [invitationToDelete, setInvitationToDelete] = useState<Invitation | null>(null);
  const perPage = 10;

  useEffect(() => {
    loadInvitations();
  }, [currentPage, statusFilter]);

  const loadInvitations = async () => {
    await getInvitations(currentPage, perPage, statusFilter, searchTerm);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadInvitations();
  };

  const handleDelete = async () => {
    try {
      await deleteInvitation(invitationToDelete?.id!);
      alertSuccess("Invitation supprimée avec succès");
      loadInvitations();
    } catch (error) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleResendEmail = async (invitation: Invitation) => {
    try {
      await resendEmail(invitation.id);
      alertSuccess("Email renvoyé avec succès");
      loadInvitations();
    } catch (error) {
      alertError("Erreur lors du renvoi de l'email");
    }
  };

  const handleRegenerateLink = async (invitation: Invitation) => {
    try {
      await regenerateLink(invitation.id);
      alertSuccess("Lien régénéré avec succès");
      loadInvitations();
    } catch (error) {
      alertError("Erreur lors de la régénération du lien");
    }
  };

  const handleCopyLink = (invitation: Invitation) => {
    const link = invitation.short_link || window.location.origin + "/i/" + (invitation.short_code || invitation.id);
    navigator.clipboard.writeText(link);
    alertSuccess("Lien copié dans le presse-papier");
  };

  const getActionsMenu = (invitation: Invitation): Menu[] => {
    const menus: Menu[] = [];

    if (invitation.status === "pending" && !invitation.is_expired) {
      menus.push({
        label: "Renvoyer l'email",
        icon: RefreshCw,
        onClick: () => handleResendEmail(invitation),
      });
      menus.push({
        label: "Régénérer le lien",
        icon: Link2,
        onClick: () => handleRegenerateLink(invitation),
      });
    }

    menus.push({
      label: "Copier le lien",
      icon: Copy,
      onClick: () => handleCopyLink(invitation),
    });

    if (invitation.status !== "accepted") {
      menus.push({
        label: "Supprimer",
        icon: Trash2,
        onClick: () => setInvitationToDelete(invitation),
      });
    }

    return menus;
  };

  if (loading && invitations.length === 0) {
    return (
      <PageListLayout title="Gestion des invitations">
        <div className="flex items-center justify-center h-64 select-none animate-fadeIn">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageListLayout>
    );
  }

  return (
    <>
      <PageListLayout
        title="Gestion des invitations"
        actions={
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors shadow-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Inviter un agent
          </button>
        }
      >
        <div className="p-6 select-none animate-fadeIn">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Rechercher par email..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all bg-white"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="accepted">Acceptées</option>
              <option value="expired">Expirées</option>
            </select>

            <button
              onClick={handleSearch}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Filtrer
            </button>
          </div>

          {/* Invitation List */}
          <InvitationsList
            invitations={invitations}
            onAction={(invitation) => {
              setSelectedInvitation(invitation);
            }}
            selectable={true}
          />

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-gray-500">
                Page {pagination.currentPage} sur {pagination.lastPage} ({pagination.total} invitations)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(pagination.lastPage, currentPage + 1))}
                  disabled={currentPage === pagination.lastPage}
                  className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </PageListLayout>

      {/* Create Invitation Modal */}
      <InviteAgentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={loadInvitations}
      />

      <DeleteConfirmationModal
        isOpen={!!invitationToDelete}
        onClose={() => setInvitationToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer l'invitation"
        message={`Êtes-vous sûr de vouloir supprimer l'invitation pour "${invitationToDelete?.email}" ? Cette action est irréversible.`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
        cancelText="Annuler"
      />

      {/* Menu modal volant (Actions d'une invitation) */}
      {selectedInvitation && (
        <MenuModal
          title={`Invitation - ${selectedInvitation.email}`}
          isOpen={true}
          onClose={() => setSelectedInvitation(null)}
          icon={null}
          menu={getActionsMenu(selectedInvitation)}
        />
      )}
    </>
  );
}